import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { StatusCode } from "api/protocols";
import moment from "moment";
import classNames from "classnames";
import { capitalize } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";
import ReactDOM from "react-dom";
import { isEqualWith } from "lodash";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Drawer from "components/drawerSlide/drawer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../reservation/editSegments.scss";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import Checkbox from "components/form/checkbox/Checkbox";
import { ILocation, ISegment, ITemplate } from "redux/reducers/models/reservations";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import { IProduct, IVariant } from "redux/reducers/models/product";
import Spin from "components/spin/spin";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { useAddFolderProductsPicker } from "hooks/folders/useAddFolderProductsPicker";
import { Badge } from "components/badge/Badge";
import {
  DeleteReservationTemplateSegments,
  GetReservationLocation,
  GetReservationTemplate,
  IIntervalSection,
  PutReservationTemplate,
  PutTemplateSegments,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import SelectNew from "components/select/SelectNew";
import TimePick from "components/timePick/TimePick";
import { unitLabel, applySelectionSubtitle, summarizeLabels } from "./unitLabel";
import { useSegmentsBesideDrawer } from "../../reservation/useSegmentsBesideDrawer";
import YesNoSetting from "../../reservation/YesNoSetting";

interface IAttachedVariant {
  variant: IVariant;
  product: IProduct;
  bookingMinutes: number | null;
}

interface IEditTemplateState {
  locations: Array<ILocation>;
  selectedLocationIds: number[];
  template: ITemplate;
  segments: { [key: string]: ISegment[] };
  leagues: Array<ILeague>;
  tournaments: Array<ITournament>;
  tournament_id: number;
  league_id: number;
  blocked_type: string;
  web_booking_enabled: boolean | null;
  credit_card_required: boolean | null;
  pre_paid_required: boolean;
  attachedVariants: Array<IAttachedVariant>;
  productsEdited: boolean;
  productsModalVisible: boolean;
}

interface IFilterState {
  tournamentSearchValue: string;
  tournamentSearching: boolean;
  selectedTournament: ITournament;
  leagueSearchValue: string;
  leagueSearching: boolean;
  selectedLeague: ILeague;
}

const intervalOptions = [30, 60];
const PRODUCT_SELECT_LIMIT = 5;

export default function ReservationTemplateEdit() {
  const { templateId } = useParams<{ templateId: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { Option } = Select;

  const blockedTypes = [t("open"), t("blocked")];
  const [state, setState] = useState<IEditTemplateState>({
    locations: null,
    selectedLocationIds: [],
    template: null,
    segments: null,
    leagues: null,
    tournaments: null,
    tournament_id: -1,
    league_id: -1,
    blocked_type: "open",
    web_booking_enabled: null,
    credit_card_required: null,
    pre_paid_required: false,
    attachedVariants: [],
    productsEdited: false,
    productsModalVisible: false,
  });

  const [filters, setFilters] = useState<IFilterState>({
    tournamentSearchValue: "",
    tournamentSearching: false,
    selectedTournament: null,
    leagueSearchValue: "",
    leagueSearching: false,
    selectedLeague: null,
  });

  const [segments, setSegments] = useState<Array<ISegment>>(undefined);
  const [segmentsBeforeChanges, setSegmentsBeforeChanges] = useState<Array<ISegment>>(undefined);
  const [editSegments, setEditSegments] = useState<Array<number>>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const segmentsContainerRef = useSegmentsBesideDrawer(settingsOpen);
  const [settingsFormKey, setSettingsFormKey] = useState(0);

  const [intervalState, setIntervalState] = useState<IIntervalSection[]>([
    { start_time: "07:00", end_time: "19:30", interval: 30 },
  ]);

  const [selectedSegmentId, setSelectedSegmentId] = useState<number>(null);

  const productPicker = useAddFolderProductsPicker({
    open: state.productsModalVisible,
    maxSelections: PRODUCT_SELECT_LIMIT,
    includeVariants: true,
    countVariants: true,
    onError: message => dispatch(showError(t(message))),
  });

  useEffect(() => {
    void loadTemplate();
  }, []);

  async function loadTemplate() {
    const templateRes = await GetReservationTemplate({ id: Number(templateId) }, true);

    if (templateRes.status !== StatusCode.OK) {
      return;
    }

    const formattedSegments = templateRes.data[0].segments
      ?.sort(
        (a: ISegment, b: ISegment) => a?.start_time?.localeCompare(b?.start_time) || a?.location_id - b?.location_id,
      )
      .reduce((formattedSegments: { [key: string]: Array<any> }, currentSegment: any) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      }, {});

    const locationRes = await GetReservationLocation({ reservation_module_id: templateRes.data[0].module_id }, false);
    if (locationRes.status !== StatusCode.OK) {
      return;
    }

    const leaguesRes = await GetLeague(null, false);
    if (leaguesRes.status !== StatusCode.OK) {
      return;
    }

    const tournamentRes = await GetTournament(null, false);
    if (tournamentRes.status !== StatusCode.OK) {
      return;
    }

    const sortedSegments: ISegment[] = templateRes.data[0].segments.sort(
      (a: ISegment, b: ISegment) => a?.start_time?.localeCompare(b?.start_time) || a?.location_id - b?.location_id,
    );

    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        template: templateRes.data[0],
        locations: locationRes.data,
        selectedLocationIds: (() => {
          const locationIds: number[] = [];
          if (Array.isArray(locationRes.data)) {
            for (const location of locationRes.data as ILocation[]) {
              const id = Number(location.id);
              if (Number.isFinite(id)) {
                locationIds.push(id);
              }
            }
          }
          const kept = prevState.selectedLocationIds.filter(id => locationIds.includes(id));
          if (kept.length > 0) {
            return kept;
          }
          return locationIds.length > 0 ? [locationIds[0]] : [];
        })(),
        segments: formattedSegments,
        leagues: leaguesRes.data,
        tournaments: tournamentRes.data,
      }));
      setSegments(sortedSegments);
      setSegmentsBeforeChanges(sortedSegments);
    });
  }

  async function saveTemplate() {
    const changedSegments = segments?.filter((segment, index) => !isEqualWith(segment, segmentsBeforeChanges[index]));
    if (changedSegments?.length > 0) {
      const segmentRes = await PutReservationTemplate({ segments: changedSegments }, true);
      if (segmentRes?.status !== StatusCode.OK) {
        dispatch(
          showError(
            segmentRes.status === StatusCode.BAD_REQUEST
              ? String(segmentRes.data)
              : t("Error updating reservation template"),
          ),
        );
        return;
      }
      void loadTemplate();
    }

    setState(prevState => ({
      ...prevState,
      tournament_id: -1,
      league_id: -1,
      blocked_type: t("open"),
      web_booking_enabled: null,
      credit_card_required: null,
      pre_paid_required: false,
      attachedVariants: [],
      productsEdited: false,
    }));
  }

  function handleSegmentClick(segment_id: number, e: React.MouseEvent) {
    const shiftKeyPressed = e.shiftKey;

    const updatedSelectedSegmentIds = [...editSegments];
    const segmentIndex = updatedSelectedSegmentIds.indexOf(segment_id);
    if (segmentIndex === -1) {
      updatedSelectedSegmentIds.push(segment_id);
      setSettingsOpen(true);

      //Shift key not pressed - set state of last selected segment
      if (!shiftKeyPressed) {
        setSelectedSegmentId(segment_id);
      } else {
        //Shift key pressed - set range of segments
        void setSegmentSelectRange(segment_id);
        return;
      }
    } else {
      updatedSelectedSegmentIds.splice(segmentIndex, 1);
      if (updatedSelectedSegmentIds?.length > 0) {
        setSelectedSegmentId(updatedSelectedSegmentIds[updatedSelectedSegmentIds?.length - 1]);
      } else {
        setSelectedSegmentId(null);
      }
    }
    setEditSegments(updatedSelectedSegmentIds);
  }

  function setSegmentSelectRange(currentSegmentId: number) {
    const segmentsArray = [...segments];
    const currentSegmentIndex = segmentsArray?.findIndex(segment => segment?.id === currentSegmentId);
    const prevSegmentIndex = segmentsArray?.findIndex(segment => segment?.id === selectedSegmentId);

    if (currentSegmentIndex !== -1 && prevSegmentIndex !== -1) {
      //Only select segments in the same location
      const locationId = segmentsArray[prevSegmentIndex]?.location_id;
      let segmentRange: Array<number>;
      if (currentSegmentIndex > prevSegmentIndex) {
        segmentRange = segmentsArray
          .slice(prevSegmentIndex, currentSegmentIndex + 1)
          ?.filter(filteredSegment => filteredSegment?.location_id === locationId)
          ?.map(segment => Number(segment?.id));
      } else {
        segmentRange = segmentsArray
          .slice(currentSegmentIndex, prevSegmentIndex + 1)
          ?.reverse()
          ?.filter(filteredSegment => filteredSegment?.location_id === locationId)
          ?.map(segment => Number(segment?.id));
      }
      setSettingsOpen(true);
      setEditSegments(prevState => [...prevState, ...segmentRange]);
    } else {
      setSelectedSegmentId(null);
      void deselectAllSegments();
    }
  }

  function selectAllSegments() {
    const allSegmentIds = segments?.map(segment => Number(segment?.id));
    setSettingsOpen(true);
    setEditSegments(allSegmentIds);
  }

  function segmentIdsForLocation(locationId: number) {
    return (segments ?? [])
      .filter(segment => segment?.location_id === locationId && segment?.id != null)
      .map(segment => Number(segment.id));
  }

  function toggleLocationSegments(locationId: number) {
    const ids = segmentIdsForLocation(locationId);
    if (ids.length === 0) {
      return;
    }
    const allSelected = ids.every(id => editSegments.includes(id));
    if (allSelected) {
      setEditSegments(prev => prev.filter(id => !ids.includes(id)));
      return;
    }
    setSettingsOpen(true);
    setEditSegments(prev => [...prev, ...ids.filter(id => !prev.includes(id))]);
  }

  function clearSelection() {
    deselectAllSegments();
    setSettingsOpen(false);
  }

  function clearSettings() {
    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({
        ...prevState,
        tournament_id: -1,
        league_id: -1,
        blocked_type: "open",
        web_booking_enabled: null,
        credit_card_required: null,
        attachedVariants: [],
        productsEdited: false,
      }));
      setFilters(prevState => ({
        ...prevState,
        tournamentSearchValue: "",
        selectedTournament: null,
        leagueSearchValue: "",
        selectedLeague: null,
      }));
      setSettingsFormKey(key => key + 1);
    });
  }

  function settingsSubtitle() {
    if (!editSegments.length || !segments?.length) {
      return undefined;
    }

    const selected = segments.filter(segment => editSegments.includes(Number(segment.id)));
    const groups = (state.locations ?? [])
      .map(location => ({
        bay: location.title,
        times: selected
          .filter(segment => segment.location_id === location.id)
          .map(segment => moment(segment.start_time, "HH:mm:ss"))
          .filter(time => time.isValid())
          .sort((a, b) => a.valueOf() - b.valueOf())
          .map(time => time.format("h:mm A")),
      }))
      .filter(group => group.times.length > 0);

    return applySelectionSubtitle(groups, t);
  }

  function deselectAllSegments() {
    ReactDOM.unstable_batchedUpdates(() => {
      setEditSegments([]);
      setSelectedSegmentId(null);
    });
  }

  function fieldsFromAttachedVariants(attached: Array<IAttachedVariant>, includeEmpty = false) {
    if (!attached?.length) {
      if (!includeEmpty) {
        return null;
      }

      return {
        variants: [],
        product_variant_ids: [],
        product_variant_id: null,
        product_title: null,
        variant_title: null,
        variant_price: null,
      };
    }

    const variants = attached.map(({ variant, product, bookingMinutes }) => ({
      product_variant_id: variant.id,
      product_title: product?.preferred_title ? product.preferred_title : product?.title,
      variant_title: variant.title,
      variant_price: variant.price,
      booking_minutes: bookingMinutes ?? null,
    }));

    return {
      variants,
      product_variant_ids: variants.map(item => item.product_variant_id),
      product_variant_id: variants[0].product_variant_id,
      product_title: variants[0].product_title,
      variant_title: variants[0].variant_title,
      variant_price: variants[0].variant_price,
    };
  }

  function replaceSegments(updatedSegments: Array<ISegment>) {
    const formattedSegments = updatedSegments?.reduce(
      (groupedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...groupedSegments,
          [currentSegment.start_time]: groupedSegments[currentSegment.start_time]
            ? [...groupedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );

    setEditSegments([]);
    setSegments(updatedSegments);
    setState(prevState => ({ ...prevState, segments: formattedSegments }));
  }

  function handleApplyButton() {
    if (filters?.selectedLeague && filters?.selectedTournament) {
      dispatch(showError(t("Cannot apply both League and Tournament at the same time")));
      return;
    }

    const variantFields = fieldsFromAttachedVariants(state.attachedVariants, state.productsEdited);
    const updatedSegments = [...segments];
    editSegments?.forEach(segmentId => {
      const currentSegmentIndex = segments?.findIndex(segment => segment?.id === segmentId);
      const currentSegment = segments[currentSegmentIndex];

      if (currentSegmentIndex !== -1) {
        const updatedCurrentSegment: any = {
          ...currentSegment,
          blocked_type: state?.blocked_type !== t("none") ? state?.blocked_type : currentSegment?.blocked_type,
          league_id:
            state?.league_id >= 1 ? state?.league_id : state?.league_id === -1 ? null : currentSegment?.league_id,
          tournament_id:
            state?.tournament_id >= 1
              ? state?.tournament_id
              : state?.tournament_id === -1
              ? null
              : currentSegment?.tournament_id,
          credit_card_required:
            state.credit_card_required != null ? state.credit_card_required : currentSegment?.credit_card_required,
          web_booking_enabled:
            state.web_booking_enabled != null ? state.web_booking_enabled : currentSegment?.web_booking_enabled,
          ...(variantFields ?? {}),
        };

        updatedSegments[currentSegmentIndex] = updatedCurrentSegment;
      }
    });

    replaceSegments(updatedSegments);
    setSettingsOpen(false);
  }

  function handleDropDownChange(value: string, id: string) {
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleTournamentChange(id: string, tournament: ITournament) {
    setState(prevState => ({ ...prevState, [id]: tournament?.id }));
    setFilters(prevState => ({ ...prevState, tournamentSearchValue: "", selectedTournament: tournament }));
  }

  function handleLeagueChange(id: string, league: ILeague) {
    setState(prevState => ({ ...prevState, [id]: league?.id }));
    setFilters(prevState => ({ ...prevState, leagueSearchValue: "", selectedLeague: league }));
  }

  function displayLeagues() {
    if (filters?.leagueSearchValue !== "") {
      return state?.leagues.filter(league =>
        league.name.toLowerCase().includes(filters.leagueSearchValue.toLowerCase()),
      );
    } else {
      return state?.leagues;
    }
  }

  function displayTournaments() {
    if (filters?.tournamentSearchValue !== "") {
      return state?.tournaments.filter(tournament =>
        tournament.name.toLowerCase().includes(filters.tournamentSearchValue.toLowerCase()),
      );
    } else {
      return state?.tournaments;
    }
  }

  function unsavedChangesExist() {
    return !isEqualWith(segments, segmentsBeforeChanges);
  }

  function cancelUnsavedChanges() {
    const formattedSegments = segmentsBeforeChanges?.reduce(
      (formattedSegments: { [key: string]: Array<ISegment> }, currentSegment: ISegment) => {
        return {
          ...formattedSegments,
          [currentSegment.start_time]: formattedSegments[currentSegment.start_time]
            ? [...formattedSegments[currentSegment.start_time], currentSegment]
            : [currentSegment],
        };
      },
      {},
    );
    ReactDOM.unstable_batchedUpdates(() => {
      setSegments(segmentsBeforeChanges);
      setState(prevState => ({ ...prevState, segments: formattedSegments }));
    });
  }

  function productsFromAttachedVariants(attached: Array<IAttachedVariant>): Array<IProduct> {
    const products: Array<IProduct> = [];
    attached.forEach(({ variant, product }) => {
      const existing = products.find(item => item.id === product.id);
      if (existing) {
        existing.variants = [...(existing.variants ?? []), variant];
        return;
      }
      products.push({ ...product, variants: [variant] });
    });
    return products;
  }

  function attachedVariantsFromProducts(
    products: Array<IProduct>,
    previous: Array<IAttachedVariant> = [],
  ): Array<IAttachedVariant> {
    const minutesByVariantId = new Map(previous.map(item => [item.variant.id, item.bookingMinutes ?? null]));
    const attached: Array<IAttachedVariant> = [];
    products.forEach(product => {
      (product.variants ?? []).forEach(variant => {
        attached.push({
          variant,
          product,
          bookingMinutes: minutesByVariantId.get(variant.id) ?? null,
        });
      });
    });
    return attached;
  }

  function openProductModal() {
    productPicker.openPicker({
      products: productsFromAttachedVariants(state.attachedVariants),
      quantities: {},
    });
    setState(prevState => ({ ...prevState, productsModalVisible: true }));
  }

  function attachProducts(products: Array<IProduct>) {
    setState(prevState => ({
      ...prevState,
      attachedVariants: attachedVariantsFromProducts(products, prevState.attachedVariants),
      productsEdited: true,
      productsModalVisible: false,
    }));
    productPicker.resetPicker();
  }

  function detachVariant(variantId: number) {
    setState(prevState => ({
      ...prevState,
      attachedVariants: prevState.attachedVariants.filter(item => item.variant.id !== variantId),
      productsEdited: true,
    }));
  }

  function setBookingMinutes(variantId: number, bookingMinutes: number | null) {
    setState(prevState => ({
      ...prevState,
      productsEdited: true,
      attachedVariants: prevState.attachedVariants.map(item =>
        item.variant.id === variantId ? { ...item, bookingMinutes } : item,
      ),
    }));
  }

  function closeProductModal() {
    setState(prevState => ({
      ...prevState,
      productsModalVisible: false,
    }));
    productPicker.resetPicker();
  }

  async function deleteSelectedSegments() {
    const deleteSegmentRes = await DeleteReservationTemplateSegments(
      {
        template_id: state?.template?.id,
        selectedSegments: editSegments,
      },
      true,
    );

    if (deleteSegmentRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error occurred deleting segments")));
      return;
    }

    dispatch(showSuccess(t("Segments deleted successfully")));

    setEditSegments([]);
    void loadTemplate();
  }

  const primaryAction = {
    content: t("Save Template"),
    action: saveTemplate,
  };

  function handleUpdateIntervals(sectionIndex: number, key: string | number, value: string | number) {
    setIntervalState(prevState => {
      const sections = [...prevState];
      sections[sectionIndex] = { ...sections[sectionIndex], [key]: value };
      return sections;
    });
  }

  function toggleGenerateLocation(locationId: number) {
    setState(prevState => ({
      ...prevState,
      selectedLocationIds: prevState.selectedLocationIds.includes(locationId)
        ? prevState.selectedLocationIds.filter(id => id !== locationId)
        : [...prevState.selectedLocationIds, locationId],
    }));
  }

  async function applyIntervals() {
    if (state.selectedLocationIds.length === 0) {
      dispatch(showError(t("Select a {{name}}", { name: unitLabel(state.template?.module?.title, t("Location")) })));
      return;
    }

    for (const locationId of state.selectedLocationIds) {
      const updateTemplateSegmentsRes = await PutTemplateSegments(
        {
          template_id: state?.template?.id,
          location_id: locationId,
          interval_sections: [intervalState[0]],
        },
        true,
      );

      if (updateTemplateSegmentsRes?.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating template segments")));
        void loadTemplate();
        return;
      }
    }

    dispatch(showSuccess(t("Segments applied successfully")));
    void loadTemplate();
  }

  const generateLocationLabel = summarizeLabels(
    state.selectedLocationIds.map(id => state.locations?.find(location => location.id === id)?.title ?? ""),
  );

  return (
    <>
    <Page
      title={t("Edit Activity Template")}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Templates"),
          url: "/admin/settings/reservations/templates",
        },
      ]}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveTemplate,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          {editSegments.length > 0 ? (
            <div className="flex items-center gap-2" style={{ padding: "16px 24px 0 24px" }}>
              <p className="font-medium" style={{ padding: "8px 18px 8px 0" }}>
                {editSegments.length} {t("selected")}
              </p>
              <div className="edit-segments-selection-divider" />
              <Button size="medium" type="text" onClick={selectAllSegments}>
                {t("Select all")}
              </Button>
              <div className="edit-segments-selection-divider" />
              <Button size="medium" type="text" onClick={clearSelection}>
                <FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear")}
              </Button>
              <div className="edit-segments-selection-divider" />
              <Button
                size="medium"
                type="text"
                onClick={deleteSelectedSegments}
                disabled={editSegments.length === 0}
              >
                {t("Delete Selected")}
              </Button>
              {!settingsOpen ? (
                <>
                  <div className="edit-segments-selection-divider" />
                  <Button size="medium" type="text" onClick={() => setSettingsOpen(true)}>
                    {t("Open settings")}
                  </Button>
                </>
              ) : null}
            </div>
          ) : !settingsOpen ? (
            <div>
              <Button size="medium" type="text" onClick={() => setSettingsOpen(true)}>
                {t("Open settings")}
              </Button>
            </div>
          ) : null}
          <div className="edit-segments">
            <div className="segments-container" ref={segmentsContainerRef}>
              <table className="ui-table ui-table-condensed ui-table-separated-horizontal segments-table">
                <thead>
                  <tr className="segments-table-header">
                    <td className="segments-table-times-header">
                      {t("secure.facility.reservation.edit_segments.027")}
                    </td>
                    {state?.locations?.map((location: ILocation) => {
                      const locationIds = segmentIdsForLocation(location.id);
                      const selectedCount = locationIds.filter(id => editSegments.includes(id)).length;
                      return (
                        <td key={location.id}>
                          <div className="segments-table-location">
                            <Checkbox
                              id={`template-location-${location.id}`}
                              size="small"
                              checked={locationIds.length > 0 && selectedCount === locationIds.length}
                              indeterminate={selectedCount > 0 && selectedCount < locationIds.length}
                              disabled={locationIds.length === 0}
                              onChange={() => toggleLocationSegments(location.id)}
                            />
                            <span>{location.title}</span>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {state.segments ? (
                    Object.keys(state?.segments)?.map((segmentKey, segmentKeyIndex) => {
                      return (
                        <tr key={segmentKeyIndex}>
                          <td className="time-text">{moment(segmentKey, "hh:mm:ss").format("h:mm A")}</td>
                          {state?.locations?.map((location: ILocation) => {
                            const segment = state?.segments[segmentKey]?.find(
                              (searchSegment: ISegment) => searchSegment?.location_id === location?.id,
                            );
                            if (!segment) {
                              return <td key={location.id} className="segment-unavailable" />;
                            }
                            const blocked = segment.blocked_type === "blocked" || segment.blocked_type === "booked";
                            const products = segment.variants?.length
                              ? segment.variants
                              : segment.product_variant_id
                              ? [
                                  {
                                    product_variant_id: segment.product_variant_id,
                                    product_title: segment.product_title,
                                    variant_title: segment.variant_title,
                                    variant_price: segment.variant_price,
                                    booking_minutes: null as number | null,
                                  },
                                ]
                              : [];
                            return (
                              <td
                                key={location.id}
                                className={classNames(blocked ? "segment-disabled" : "segment-available", {
                                  "segment-selected": editSegments.includes(Number(segment.id)),
                                  "segment-league": !!segment.league_id,
                                  "segment-tournament": !!segment.tournament_id,
                                })}
                                onClick={(e: React.MouseEvent) => handleSegmentClick(Number(segment.id), e)}
                              >
                                <div className="segment-container">
                                  <p className="blocked-type-text">{capitalize(segment.blocked_type)}</p>
                                  {products.length > 0 ? (
                                    <div className="segment-products">
                                      {products.map(variant => (
                                        <div key={variant.product_variant_id} className="variant-group">
                                          <p className="variant-title">{variant.product_title}</p>
                                          {variant.product_title !== variant.variant_title && (
                                            <p className="variant-title">{variant.variant_title}</p>
                                          )}
                                          <LocaleCurrency style={{ fontSize: "0.7rem" }} amount={variant.variant_price} />
                                          {variant.booking_minutes === 30 || variant.booking_minutes === 60 ? (
                                            <p className="variant-title">
                                              {t("{{count}} minutes", { count: variant.booking_minutes })}
                                            </p>
                                          ) : null}
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                  <div className="segment-options-container">
                                    {segment.league_id ? <FontAwesomeIcon size="1x" icon={["far", "users"]} /> : null}
                                    {segment.tournament_id ? <FontAwesomeIcon size="1x" icon={["far", "trophy"]} /> : null}
                                    {segment.credit_card_required ? (
                                      <FontAwesomeIcon size="1x" icon={["far", "credit-card"]} />
                                    ) : null}
                                    {segment.web_booking_enabled ? (
                                      <FontAwesomeIcon size="1x" icon={["far", "earth-americas"]} />
                                    ) : null}
                                  </div>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  ) : (
                    <div
                      style={{ marginTop: "1rem", marginBottom: "1rem", pointerEvents: "none", textAlign: "center" }}
                    >
                      <span style={{ display: "inline-flex", transform: "scale(2)" }}>
                        <Spin />
                      </span>
                    </div>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card.Section>
      </Card>
    </Page>
    <Drawer
      open={settingsOpen}
      title={t("Apply settings")}
      subtitle={settingsSubtitle()}
      onClose={() => setSettingsOpen(false)}
      onOk={handleApplyButton}
      okText={t("Apply")}
      okDisabled={editSegments.length === 0}
      backDropClose
      hideBackdrop
      showCancel
      cancelText={t("Cancel")}
      cancelButtonType="text"
      secondaryActions={[{ label: t("Clear all"), action: clearSettings }]}
    >
      <div key={settingsFormKey} className="edit-segments-drawer">
      <Drawer.Section bordered>
        <Form>
          <FormLayout>
            <FormLayout.Group>
              <Select
                label={t("Availability")}
                onChange={(value: string) => handleDropDownChange(value, "blocked_type")}
                defaultValue={state?.blocked_type}
              >
                {blockedTypes?.map((blocked_type, index) => {
                  return (
                    <Option key={index} value={blocked_type}>
                      {blocked_type !== "none" ? capitalize(blocked_type) : ""}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("League")}
                onChange={(_, league: ILeague) => handleLeagueChange("league_id", league)}
                defaultValue={filters?.selectedLeague?.id}
                placeholder={t("Search leagues...")}
                showSearch
                searchValue={filters.leagueSearchValue || ""}
                showDropDownOnFocus={true}
                searching={filters.leagueSearching}
                onSearch={(query: string) =>
                  setFilters(prevState => ({
                    ...prevState,
                    leagueSearchValue: query,
                    selectedLeague: query === "" ? null : prevState.selectedLeague,
                  }))
                }
              >
                <Option key={-1} value={-1} name={t("None")}>
                  {t("None")}
                </Option>
                {displayLeagues()?.map((league: ILeague, index: number) => {
                  return (
                    <Option key={index} value={league?.id} extraValues={league} name={league.name}>
                      {league?.name}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label={t("Tournament")}
                onChange={(_, tournament: ITournament) => handleTournamentChange("tournament_id", tournament)}
                defaultValue={state?.tournament_id}
                placeholder={t("Search tournaments...")}
                showSearch
                searchValue={filters.tournamentSearchValue}
                showDropDownOnFocus={true}
                searching={filters.tournamentSearching}
                onSearch={(query: string) =>
                  setFilters(prevState => ({
                    ...prevState,
                    tournamentSearchValue: query,
                    selectedTournament: query === "" ? null : prevState.selectedTournament,
                  }))
                }
              >
                <Option key={-1} value={-1} name={t("None")}>
                  {t("None")}
                </Option>
                {displayTournaments()?.map((tournament: ITournament, index: number) => {
                  return (
                    <Option key={index} value={tournament?.id} extraValues={tournament} name={tournament.name}>
                      {tournament?.name}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </Form>
      </Drawer.Section>
      <Drawer.Section bordered>
        <div className="edit-segments-button-group">
          <Button size="medium" type="default" onClick={openProductModal}>
            {t("Attach Product")}
          </Button>
          {state.attachedVariants.map(attached => (
            <div key={attached.variant.id} className="edit-segments-variant-badge-container">
              <Badge type="primary">
                <span>
                  {attached.product?.preferred_title ? attached.product.preferred_title : attached.product?.title}{" "}
                  {attached.variant?.title !== attached.product?.title ? ` - ${attached.variant?.title}` : ""}{" "}
                  <LocaleCurrency amount={attached.variant?.price} />
                </span>
              </Badge>
              <SelectNew
                id={`booking-length-${attached.variant.id}`}
                className="edit-segments-booking-length"
                value={attached.bookingMinutes ?? 0}
                onChange={minutes => setBookingMinutes(attached.variant.id, minutes === 0 ? null : minutes)}
                options={[
                  { id: 0, label: t("Every booking") },
                  { id: 30, label: t("30 minutes") },
                  { id: 60, label: t("60 minutes") },
                ]}
              />
              <FontAwesomeIcon
                size="1x"
                icon={faXmark}
                onClick={() => detachVariant(attached.variant.id)}
                className="edit-segments-remove-variant"
              />
            </div>
          ))}
        </div>
        <div className="edit-segments-setting-choices">
          <YesNoSetting
            id="credit_card_required"
            label={t("Credit card required")}
            value={state.credit_card_required}
            onChange={value => setState(prevState => ({ ...prevState, credit_card_required: value }))}
          />
          <YesNoSetting
            id="web_booking_enabled"
            label={t("Web booking enabled")}
            value={state.web_booking_enabled}
            onChange={value => setState(prevState => ({ ...prevState, web_booking_enabled: value }))}
          />
        </div>
      </Drawer.Section>
      <Drawer.Section bordered>
        <div className="edit-segments-generate">
          <p className="interval-section-title">
            {generateLocationLabel
              ? t("Generate segments for {{location}}", { location: generateLocationLabel })
              : t("Generate segments")}
          </p>
        <FormLayout>
          <FormLayout.Group>
            <SelectNew
              id="generate-locations"
              label={unitLabel(state.template?.module?.title, t("Location"))}
              multiValue={state.selectedLocationIds}
              onChange={toggleGenerateLocation}
              placeholder={
                generateLocationLabel ||
                t("Select {{name}}", { name: unitLabel(state.template?.module?.title, t("Location")) })
              }
              options={(state.locations ?? []).map(location => ({ id: location.id, label: location.title }))}
              usePortal
              dropdownPosition="top"
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TimePick
              value={intervalState[0]?.start_time}
              onChange={time => handleUpdateIntervals(0, "start_time", time)}
              label={t("Start Time")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <TimePick
              value={intervalState[0]?.end_time}
              onChange={time => handleUpdateIntervals(0, "end_time", time)}
              label={t("End Time")}
            />
          </FormLayout.Group>
          <FormLayout.Group>
            <SelectNew
              id="interval"
              label={t("Interval (minutes)")}
              value={intervalState[0]?.interval}
              onChange={interval => handleUpdateIntervals(0, "interval", interval)}
              options={intervalOptions.map(interval => ({ id: interval, label: interval }))}
              placeholder={t("Select Interval")}
            />
          </FormLayout.Group>
        </FormLayout>
          <Button block size="medium" type="secondary" onClick={() => void applyIntervals()}>
            {t("Generate segments")}
          </Button>
        </div>
      </Drawer.Section>
      </div>
    </Drawer>
    <AddFolderProductsModal
      open={state.productsModalVisible}
      onClose={closeProductModal}
      onAdd={attachProducts}
      search={productPicker.search}
      onSearchChange={productPicker.setSearch}
      browseFilter={productPicker.browseFilter}
      onBrowseFilterChange={productPicker.setBrowseFilter}
      departments={productPicker.departments}
      departmentsLoading={productPicker.departmentsLoading}
      uncategorizedCount={productPicker.uncategorizedCount}
      productsToAdd={productPicker.productsToAdd}
      availableSlotCount={PRODUCT_SELECT_LIMIT}
      totalEmptySlotCount={PRODUCT_SELECT_LIMIT}
      folderProducts={[]}
      productSearchResults={productPicker.productSearchResults}
      productSearchTotalCount={productPicker.productSearchTotalCount}
      productSearchPending={productPicker.productSearchPending}
      productSearchLoadingMore={productPicker.productSearchLoadingMore}
      productSearchSelectAllPending={productPicker.productSearchSelectAllPending}
      onLoadMoreProducts={productPicker.loadMoreProducts}
      onSelectAllMatchingProducts={productPicker.selectAllMatchingProducts}
      onSelectProduct={productPicker.handleSelectProduct}
      onUnselectProduct={productPicker.handleUnselectProduct}
      hideSlots
      showPrice
      enableVariantSelection
      title={t("Attach Product")}
      confirmText={t("Select")}
      browseOnOpen
      onCreateProduct={() => {
        closeProductModal();
        history.push("/admin/product/new");
      }}
      onGoToProducts={() => {
        closeProductModal();
        history.push("/admin/product");
      }}
    />
    </>
  );
}
